import { Injectable, inject } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import { FirebaseRealtimeDatabaseService } from '../firebase-realtime-database/firebase-realtime-database.service';
import { AnalyticsService } from '../analytics/analytics.service';

@Injectable({
  providedIn: 'root',
})
export class MovesService {
  private storageService = inject(StorageService);
  private analyticsService = inject(AnalyticsService);
  private firebaseRDBService = inject(FirebaseRealtimeDatabaseService);
  private moves: Map<string, string> = new Map();

  async getInitialMoves(roomId: string, userId: string): Promise<string[]> {
    try {
      // Get moves in Local Storage
      const key = MovesService.createKey(roomId, userId);
      const localMoves =
        (await this.storageService.getItem<string>(StorageService.movesKey(key))) ?? '';

      // Get moves on Firebase
      const databaseMoves = (await this.firebaseRDBService.getMoves(userId, roomId)) ?? '';

      // If local moves is longer than the database, and both strings begin with the same sequence
      if (localMoves.length > databaseMoves.length && localMoves.startsWith(databaseMoves)) {
        return localMoves.split(',').filter(Boolean);
      }
      return databaseMoves.split(',').filter(Boolean);
    } catch (error) {
      await this.analyticsService.handleError(error, 'Get moves in db');
      return [];
    }
  }

  getMoves(roomId: string, userId: string): string[] {
    const key = MovesService.createKey(roomId, userId);
    const moves = this.moves.get(key);
    return moves?.split(',').filter(Boolean) ?? [];
  }

  async setMoves(roomId: string, userId: string, moves: string[]): Promise<void> {
    const movesRaw = moves.join(',');

    // Save in ram
    const key = MovesService.createKey(roomId, userId);
    this.moves.set(key, movesRaw);

    // Save to local storage
    await this.storageService.setItem<string>(StorageService.movesKey(key), movesRaw);

    // Backup on firebase rtb
    await this.firebaseRDBService.setMoves(userId, roomId, movesRaw);
  }

  signOut() {
    this.moves = new Map();
  }

  private static createKey(roomId: string, userId: string): string {
    return `${userId}_${roomId}`;
  }
}
